import React, { memo, useCallback, useEffect } from 'react';
import { TouchableWithoutFeedback, View, StyleSheet } from 'react-native';
import { rem } from 'design-system';
import IconSearch from 'design-system/icons/IconSearch';
import { Amplitude } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setInvestWalkthroughStep } from 'actions/walkthrough';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectInvestWalkthroughStep, selectIsInvestWalkthroughActive } from 'reducers/selectors/walkthrough';
import useInvestWalkthroughItems from 'features/feed/components/walkthrough/invest/useInvestWalkthroughItems';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Animated, { Easing, withRepeat, withTiming, interpolate, useSharedValue, cancelAnimation, useAnimatedStyle, } from 'react-native-reanimated';
import { isWeb } from '../../../constants';
const FloatingSearchButton = ({ source, hide, style }) => {
    const styles = useStyles(styleSet);
    const { paddingHorizontal } = useAppFrameDimensions(isWeb);
    // On web, keep the button inside the content column rather than at the window edge
    const webPositionStyle = isWeb ? { right: rem(16) + paddingHorizontal } : undefined;
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const sharedValue = useSharedValue(0);
    const investWalkthroughStep = useAppSelector(selectInvestWalkthroughStep);
    const isInvestWalkthroughActive = useAppSelector(selectIsInvestWalkthroughActive);
    const investWalkthroughItems = useInvestWalkthroughItems();
    const step = investWalkthroughItems[investWalkthroughStep];
    const showBtn = !hide && (!isInvestWalkthroughActive || (isInvestWalkthroughActive && step?.title === 'Let’s find a fund for you'));
    const onGoToSearch = useCallback(() => {
        if (isInvestWalkthroughActive) {
            dispatch(setInvestWalkthroughStep({
                step: 3,
            }));
            navigation.navigateDeprecated('InvestDiscover');
            return;
        }
        Amplitude.logEvent('Open Discovery', {
            source,
        });
        navigation.navigateDeprecated('InvestDiscover');
    }, [isInvestWalkthroughActive, source]);
    const startAnimation = useCallback(() => {
        sharedValue.value = withRepeat(withTiming(1, {
            duration: 600,
            easing: Easing.out(Easing.ease),
        }), -1, true);
    }, [sharedValue]);
    useEffect(() => {
        const timeout = setTimeout(() => {
            startAnimation();
        }, 500);
        return () => {
            clearTimeout(timeout);
        };
    }, [startAnimation]);
    const animatedStyles = useAnimatedStyle(() => ({
        transform: [{ scale: interpolate(sharedValue.value, [0, 1], [0.89, 1]) }],
    }), [sharedValue]);
    const stopAnimation = useCallback(() => {
        cancelAnimation(sharedValue);
    }, [sharedValue]);
    const onPressIn = useCallback(() => {
        stopAnimation();
        sharedValue.value = withTiming(0, {
            duration: 100,
            easing: Easing.out(Easing.ease),
        });
    }, [stopAnimation, sharedValue]);
    const onPressOut = useCallback(() => {
        stopAnimation();
        startAnimation();
    }, [startAnimation, stopAnimation]);
    return (<View style={showBtn ? null : styles.noOpacity} pointerEvents={showBtn && !isInvestWalkthroughActive ? 'auto' : 'none'}>
      <View style={[styles.container, webPositionStyle, style]}>
        <Animated.View style={[styles.pulsatingView, animatedStyles]}/>
        <TouchableWithoutFeedback onPress={onGoToSearch} onPressIn={onPressIn} onPressOut={onPressOut}>
          <View style={styles.button}>
            <IconSearch alt/>
          </View>
        </TouchableWithoutFeedback>
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    noOpacity: {
        opacity: 0,
    },
    container: {
        zIndex: 1,
        width: rem(74),
        right: rem(16),
        height: rem(74),
        bottom: rem(16),
        alignItems: 'center',
        position: 'absolute',
        justifyContent: 'center',
    },
    button: {
        width: rem(56),
        height: rem(56),
        borderRadius: rem(28),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.buttons.brand,
    },
    pulsatingView: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 0,
        opacity: 0.15,
        borderRadius: rem(9999),
        backgroundColor: colors.buttons.brand,
    },
}));
export default memo(FloatingSearchButton);
